import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import {
  TEMPLATE_VARIABLE_NAMES,
  type ModelTemplateBody,
  type ResponseTemplate,
} from '@erp/shared';
import { api } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Input, Label, Select, Textarea } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Badge, Dialog, EmptyState, Skeleton } from '@/components/ui/misc';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { errorMessage } from '@/features/desks/desks-queries';
import { DeskRowAction } from '@/features/desks/desk-stages-section';
import { desksListQuery } from '@/features/tickets/tickets-queries';
import { modelTemplatesOptions, MODEL_KIND_LABELS, type ModelKind } from './models-queries';
import { EmailModelsTab } from './email-models-tab';

const TAB_TRIGGER_CLASSES =
  'rounded-none border-b-2 border-transparent px-3 pb-3 data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none';

/**
 * Página /models — modelos de resposta (composer de comunicação), de
 * apontamento e de e-mail (conteúdo da antiga /templates) em três abas.
 */
export function ModelsPage() {
  const [tab, setTab] = useState('respostas');

  return (
    <div className="overflow-hidden rounded-lg border bg-card shadow-xs">
      <Tabs value={tab} onValueChange={setTab}>
        <div className="flex flex-wrap items-center gap-3 border-b px-4 pt-3">
          <h1 className="pb-3 text-xl leading-7 font-semibold">Modelos</h1>
          <TabsList className="ml-auto h-auto gap-1 bg-transparent p-0">
            <TabsTrigger value="respostas" className={TAB_TRIGGER_CLASSES}>
              Respostas
            </TabsTrigger>
            <TabsTrigger value="apontamentos" className={TAB_TRIGGER_CLASSES}>
              Apontamentos
            </TabsTrigger>
            <TabsTrigger value="email" className={TAB_TRIGGER_CLASSES}>
              E-mail
            </TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value="respostas">
          <ModelCrudTab kind="responses" />
        </TabsContent>
        <TabsContent value="apontamentos">
          <ModelCrudTab kind="appointments" />
        </TabsContent>
        <TabsContent value="email">
          <EmailModelsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ── CRUD de modelos de resposta / apontamento ───────────────────────────────

function ModelCrudTab({ kind }: { kind: ModelKind }) {
  const queryClient = useQueryClient();
  const templates = useQuery(modelTemplatesOptions(kind));
  const desks = useQuery(desksListQuery());
  const [dialog, setDialog] = useState<{ template: ResponseTemplate | null } | null>(null);

  const deskName = (deskId: number | null): string => {
    if (deskId === null) return 'Todas as mesas';
    const desk = (desks.data ?? []).find((d) => d.id === deskId);
    return desk?.displayName ?? `Mesa #${deskId}`;
  };

  const remove = useMutation({
    mutationFn: async (id: number) => {
      const res = await api<void>(`/models/${kind}/${id}`, { method: 'DELETE' });
      if (res.error) throw res.error;
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['erp', 'models', kind] }),
  });

  const list = templates.data ?? [];
  const label = MODEL_KIND_LABELS[kind];

  return (
    <div>
      <div className="flex justify-end px-4 pt-3">
        <Button size="sm" onClick={() => setDialog({ template: null })}>
          <Plus className="size-4" strokeWidth={2.5} />
          Novo modelo
        </Button>
      </div>
      {templates.isPending ? (
        <div className="space-y-2 p-4" aria-busy>
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : templates.isError ? (
        <EmptyState message={`Erro ao carregar modelos: ${errorMessage(templates.error)}`} />
      ) : list.length === 0 ? (
        <EmptyState className="h-52" message={`Nenhum modelo de ${label} cadastrado`} />
      ) : (
        <div className="overflow-x-auto p-4 pt-3">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted text-left text-[13px]">
                <th className="h-10 px-4 font-semibold">Nome</th>
                <th className="h-10 px-4 font-semibold">Mesa</th>
                <th className="h-10 px-4 font-semibold">Status</th>
                <th className="h-10 px-4 text-right font-semibold">Ações</th>
              </tr>
            </thead>
            <tbody>
              {list.map((t) => (
                <tr key={t.id} className="h-10 border-b border-divider last:border-0">
                  <td className="max-w-72 truncate px-4 font-medium">{t.name}</td>
                  <td className="max-w-56 truncate px-4 text-muted-foreground">
                    {deskName(t.deskId)}
                  </td>
                  <td className="px-4 whitespace-nowrap">
                    {t.active ? <Badge variant="success">Ativo</Badge> : <Badge>Inativo</Badge>}
                  </td>
                  <td className="px-4">
                    <div className="flex items-center justify-end gap-0.5">
                      <DeskRowAction label="Editar modelo" onClick={() => setDialog({ template: t })}>
                        <Pencil className="size-3.5" />
                      </DeskRowAction>
                      <DeskRowAction
                        label="Excluir modelo"
                        destructive
                        disabled={remove.isPending}
                        onClick={() => {
                          if (window.confirm(`Excluir o modelo "${t.name}"?`)) remove.mutate(t.id);
                        }}
                      >
                        <Trash2 className="size-3.5" />
                      </DeskRowAction>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {remove.error ? (
        <p className="px-4 pb-3 text-sm text-destructive">{errorMessage(remove.error)}</p>
      ) : null}
      {dialog ? (
        <ModelDialog kind={kind} template={dialog.template} onClose={() => setDialog(null)} />
      ) : null}
    </div>
  );
}

const TEMPLATE_VARIABLES = TEMPLATE_VARIABLE_NAMES.map((name) => `{{${name}}}`);

function ModelDialog({
  kind,
  template,
  onClose,
}: {
  kind: ModelKind;
  template: ResponseTemplate | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const desks = useQuery(desksListQuery());
  const [name, setName] = useState(template?.name ?? '');
  const [deskId, setDeskId] = useState(template?.deskId != null ? String(template.deskId) : '');
  const [body, setBody] = useState(template?.body ?? '');
  const [active, setActive] = useState(template?.active ?? true);

  const save = useMutation({
    mutationFn: async (payload: ModelTemplateBody) => {
      const res = template
        ? await api<ResponseTemplate>(`/models/${kind}/${template.id}`, {
            method: 'PUT',
            body: payload,
          })
        : await api<ResponseTemplate>(`/models/${kind}`, { method: 'POST', body: payload });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['erp', 'models', kind] });
      onClose();
    },
  });

  const valid = name.trim() && body.trim();

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    save.mutate({
      name: name.trim(),
      body,
      deskId: deskId === '' ? null : Number(deskId),
      active,
    });
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={template ? 'Editar modelo' : `Novo modelo de ${MODEL_KIND_LABELS[kind]}`}
      className="sm:max-w-2xl"
    >
      <form onSubmit={submit} className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="model-name">
              Nome <span className="text-destructive">*</span>
            </Label>
            <Input
              id="model-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="model-desk">Mesa</Label>
            <Select id="model-desk" value={deskId} onChange={(e) => setDeskId(e.target.value)}>
              <option value="">Todas as mesas</option>
              {(desks.data ?? [])
                .filter((d) => d.active || d.id === template?.deskId)
                .map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.displayName}
                  </option>
                ))}
            </Select>
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="model-body">
            Corpo <span className="text-destructive">*</span>
          </Label>
          <Textarea
            id="model-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            required
            rows={8}
            className="min-h-40"
            placeholder={'Olá {{client.name}},\n\nSobre o ticket #{{ticket.number}} — {{ticket.title}}…'}
          />
        </div>
        <div className="rounded-md border border-divider bg-muted/60 p-3">
          <p className="text-xs font-medium text-muted-foreground">Variáveis disponíveis</p>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {TEMPLATE_VARIABLES.map((v) => (
              <code
                key={v}
                className="rounded-sm border border-border bg-card px-1.5 py-0.5 font-mono text-xs"
              >
                {v}
              </code>
            ))}
          </div>
        </div>
        <label className="flex cursor-pointer items-center gap-2 text-sm font-medium">
          <Switch checked={active} onCheckedChange={setActive} />
          Modelo ativo
        </label>
        {save.error ? <p className="text-sm text-destructive">{errorMessage(save.error)}</p> : null}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" size="sm" disabled={save.isPending || !valid}>
            {save.isPending ? 'Salvando…' : 'Salvar'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
